import noop from 'lodash/noop';
import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useImmer } from 'use-immer';

import { useChat } from '@/hooks/useChat';
import { useGeoDataMyIp } from '@/hooks/useGeoDataMyIp';
import type { IUserResponse } from '@/redux/modules/auth/userMini';
import { getAuthenticatedUser } from '@/redux/selectors/auth/user';
import {
  IChatbotEvent,
  trackChatbotClosed,
  trackChatbotError,
  trackChatbotNewConverstationStarted,
  trackChatbotOpened,
  trackChatbotResetChat,
} from '@/services/analytics/chatbot';

export enum EAgentName {
  TRIP_PLANNER = 'trip-planner',
}

export enum EChatbotVariant {
  HOME = 'home',
  SRP = 'srp',
}

export enum EMessageRole {
  USER = 'user',
  MODEL = 'model',
}

export interface IChatApiRequest {
  message: string;
  agent_name: string;
  dynamic_variables: Record<string, string>;
  session_id?: string;
}

export interface IChatApiResponse {
  text: string;
  function_responses?: Record<string, IFunctionResponse>;
  session_id?: string;
}

export interface IFunctionResponse {
  text: string;
  [key: string]: unknown;
}

export interface IChatbotMessage {
  role: EMessageRole;
  text: string;
  timestamp: Date;
  function_responses?: Record<string, IFunctionResponse>;
  loading: boolean;
  error?: boolean;
}

interface IChatbotContext {
  currentLocation: string;
  messages: IChatbotMessage[];
  submitMessage: (message: string, isRetry?: boolean, retryMessageIndex?: number) => void;
  handleResendMessage: (messageIndex: number) => void;
  resetChat: () => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  loading: boolean;
  authenticatedUser: IUserResponse | null;
  sessionId: string;
  agentName: EAgentName;
  error: boolean;
  variant: EChatbotVariant;
  trackingEventData: IChatbotEvent;
}

const defaultChatbotContext: IChatbotContext = {
  currentLocation: '',
  messages: [],
  submitMessage: noop,
  handleResendMessage: noop,
  resetChat: noop,
  isOpen: false,
  setIsOpen: noop,
  loading: false,
  authenticatedUser: null,
  sessionId: '',
  agentName: EAgentName.TRIP_PLANNER,
  error: false,
  variant: EChatbotVariant.HOME,
  trackingEventData: {
    variant: EChatbotVariant.HOME,
    agentName: EAgentName.TRIP_PLANNER,
    sessionId: '',
  },
};

const ChatbotContext = createContext<IChatbotContext>(defaultChatbotContext);

interface IChatbotProviderProps {
  open: boolean;
  onClose?: () => void;
  agentName: EAgentName;
  variant: EChatbotVariant;
}

const ChatbotProvider = ({
  open,
  onClose,
  children,
  agentName,
  variant,
}: PropsWithChildren<IChatbotProviderProps>) => {
  const [hasSentIntroduction, setHasSentIntroduction] = useState(false);
  const [messages, setMessages] = useImmer<IChatbotMessage[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [error, setError] = useState(false);
  const authenticatedUser = useSelector(getAuthenticatedUser);
  const [isOpen, setIsOpen] = useState(open);
  const { sendChatMessage } = useChat(agentName);

  const { data: geoDataMyIp } = useGeoDataMyIp();
  const currentLocation = geoDataMyIp ? `${geoDataMyIp.city}, ${geoDataMyIp.state}` : '';

  const trackingEventData = useMemo(
    () => ({
      variant,
      agentName,
      sessionId,
      messageCount: messages.length,
    }),
    [variant, agentName, sessionId, messages.length],
  );

  // Effect to send introduction the first time the chatbot is opened
  useEffect(() => {
    const sendIntroduction = async () => {
      setMessages(draft => {
        return [...draft, { role: EMessageRole.MODEL, text: '', loading: true }];
      });

      sendChatMessage('Introduce yourself', sessionId, {
        currentLocation,
        userName: authenticatedUser?.first_name || '',
        todaysDate: new Date().toString(),
      })
        .then(data => {
          setMessages(draft => {
            const lastMessage = draft[draft.length - 1];
            if (lastMessage) {
              lastMessage.loading = false;
              lastMessage.text = data.text;
              lastMessage.timestamp = new Date();
            }
          });
          setSessionId(data.session_id || '');
          trackChatbotNewConverstationStarted({
            ...trackingEventData,
            sessionId: data.session_id || '',
          });
        })
        .catch((e: Error) => {
          setMessages(draft => {
            const lastMessage = draft[draft.length - 1];
            if (lastMessage) {
              lastMessage.loading = false;
              lastMessage.timestamp = new Date();
            }
            const lastUserMessage = draft.filter(m => m.role === EMessageRole.USER).at(-1);
            if (lastUserMessage) {
              lastUserMessage.error = true;
            }
          });
          setError(true);
          trackChatbotError({ ...trackingEventData, error: e?.message });
        });
    };

    if (isOpen && !hasSentIntroduction) {
      sendIntroduction();
      setHasSentIntroduction(true);
    }
  }, [
    isOpen,
    hasSentIntroduction,
    currentLocation,
    setMessages,
    authenticatedUser,
    sessionId,
    sendChatMessage,
    trackingEventData,
  ]);

  useEffect(() => {
    if (!open) {
      trackChatbotClosed(trackingEventData);
    } else {
      trackChatbotOpened(trackingEventData);
    }
    setIsOpen(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submitMessage = async (message: string, isRetry = false, retryMessageIndex?: number) => {
    if (isRetry && retryMessageIndex !== undefined) {
      // For retry: clear error states and add new loading model response
      setMessages(draft => {
        if (draft[retryMessageIndex]) {
          draft[retryMessageIndex].error = false;
        }
        setError(false);
        draft.push({ role: EMessageRole.MODEL, text: '', loading: true, timestamp: new Date() });
      });
    } else {
      // For new message: add user message and loading model response
      setMessages(draft => {
        return [
          ...draft,
          { role: EMessageRole.USER, text: message, timestamp: new Date() },
          { role: EMessageRole.MODEL, text: '', loading: true, timestamp: new Date() },
        ];
      });
    }

    sendChatMessage(message, sessionId, {
      currentLocation,
      userName: authenticatedUser?.first_name || '',
      todaysDate: new Date().toString(),
    })
      .then(data => {
        setMessages(draft => {
          // Update the last model message with the response
          const lastMessage = draft[draft.length - 1];
          if (lastMessage) {
            lastMessage.loading = false;
            lastMessage.text = data.text;
            lastMessage.function_responses = data.function_responses;
            lastMessage.timestamp = new Date();
          }
        });
        setError(false);
      })
      .catch((e: Error) => {
        setMessages(draft => {
          const lastMessage = draft[draft.length - 1];
          if (lastMessage) {
            lastMessage.loading = false;
            lastMessage.timestamp = new Date();
          }

          if (isRetry && retryMessageIndex !== undefined) {
            // Mark the original message as error again for retry
            if (draft[retryMessageIndex]) {
              draft[retryMessageIndex].error = true;
            }
          } else {
            // Mark the latest user message as error for new message
            const lastUserMessage = draft.filter(m => m.role === EMessageRole.USER).at(-1);
            if (lastUserMessage) {
              lastUserMessage.error = true;
            }
          }
        });
        setError(true);
        trackChatbotError({ ...trackingEventData, error: e?.message });
      });
  };

  const handleResendMessage = async (messageIndex: number) => {
    const messageToResend = messages[messageIndex];
    if (!messageToResend || messageToResend.role !== EMessageRole.USER) {
      return;
    }

    await submitMessage(messageToResend.text, true, messageIndex);
  };

  const resetChat = () => {
    setSessionId('');
    setMessages([]);
    setError(false);
    setHasSentIntroduction(false);

    trackChatbotResetChat(trackingEventData);
  };

  const handleSetIsOpen = (newIsOpen: boolean) => {
    setIsOpen(newIsOpen);
    if (!newIsOpen && onClose) {
      onClose();
    }
  };

  const value = {
    currentLocation,
    messages,
    submitMessage,
    handleResendMessage,
    resetChat,
    isOpen,
    setIsOpen: handleSetIsOpen,
    loading: false,
    authenticatedUser,
    sessionId,
    agentName,
    error,
    variant,
    trackingEventData,
  };

  return <ChatbotContext.Provider value={value}>{children}</ChatbotContext.Provider>;
};

export const useChatbot = () => useContext(ChatbotContext);

export default ChatbotProvider;
